import i18n from '@lib/i18n';
import environment from '../environment';
import { getNativeLanguageLabels } from '../teacher/common/languageLabels';

const { ADMINIFI_URL, HOMEROOM_PLATFORM_URL, apiBasePath } = environment;

export interface MenuSlug {
  sidebar?: string;
  topbar?: string;
}

export interface PlatformConfig {
  themeLibrary: string;
  sessionUrl: string;
  isHomeroom: boolean;
  usesOrgData: boolean;
  userMeUrl: string;
  signOutUrl: string;
  localesUrl: string | null;
  orgBySlugUrl: string | null;
  normalizeLocale: (item: K12Locale | FoundryLang, urlWithoutLang: string) => [string, unknown];
}

export interface K12Locale {
  id: string;
  language_abbreviation: string;
  [key: string]: unknown;
}

export interface FoundryLang {
  code: string;
  [key: string]: unknown;
}

/**
 * Static map of portal identifiers to HeaderMenuWrapper / Sidebar menu slugs.
 */
export const MENU_SLUGS: Record<string, MenuSlug> = {
  fit: {
    sidebar: 'everfi_admin_left',
    topbar: 'everfi_admin_navigation',
  },
  customer: {
    sidebar: 'customer_admin_left',
    topbar: 'cportal_user_navigation',
  },
  learner: {
    topbar: 'learner_navigation_menu',
  },
  ceos: {
    sidebar: 'ceos_left',
    topbar: 'ceos_navigation',
  },
  teacher: {
    sidebar: 'homeroom_teacher_left',
    topbar: 'homeroom_teacher_navigation',
  },
  next_learner: {
    topbar: 'next_learner_menu',
  },
  student: {
    topbar: 'homeroom_student_navigation',
  },
  parent: {
    topbar: 'homeroom_parent_navigation',
  },
};

/**
 * Platform configs — one entry per platform value.
 * All platform-specific branching lives here; hooks and components stay generic.
 */
const PLATFORM_CONFIGS = (slug: string | undefined): Record<string, PlatformConfig> => ({
  k12: {
    themeLibrary: 'k12',
    sessionUrl: `${HOMEROOM_PLATFORM_URL}/oauth/token/info`,
    isHomeroom: true,
    usesOrgData: false,
    userMeUrl: `${HOMEROOM_PLATFORM_URL}/api/v3/me`,
    signOutUrl: HOMEROOM_PLATFORM_URL,
    localesUrl: `${apiBasePath.HOMEROOM}/current_school/locales`,
    orgBySlugUrl: null,
    normalizeLocale: (item, urlWithoutLang) => {
      const locale = item as K12Locale;
      const key = (i18n.options as { languages?: string[] }).languages?.includes(locale.id)
        ? locale.id
        : locale.language_abbreviation;
      return [
        key,
        {
          ...locale,
          native_name: getNativeLanguageLabels([locale.id])[0],
          translation_url: `https://${window.location.hostname}/${key}${urlWithoutLang}`,
        },
      ];
    },
  },
  foundry: {
    themeLibrary: 'chakraUiTheme',
    sessionUrl: `${ADMINIFI_URL}/oauth/token/info`,
    isHomeroom: false,
    usesOrgData: true,
    userMeUrl: `${apiBasePath.ADMINIFI}/${slug ? 'users' : 'administrators'}/me`,
    signOutUrl: slug ? `${ADMINIFI_URL}/${slug}/sign_out` : `${ADMINIFI_URL}/admin/session`,
    localesUrl: slug ? `${apiBasePath.ADMINIFI}/languages` : null,
    orgBySlugUrl: slug ? `${apiBasePath.ADMINIFI}/organizations/by_slug/${slug}` : null,
    normalizeLocale: (item, urlWithoutLang) => {
      const lang = item as FoundryLang;
      return [lang.code, { ...lang, translation_url: `/${lang.code.replace('_', '-')}${urlWithoutLang}` }];
    },
  },
});

export const getPlatformConfig = (platform: string, slug: string | undefined): PlatformConfig => {
  const configs = PLATFORM_CONFIGS(slug);
  if (!(platform in configs)) throw new Error(`Unknown platform: "${platform}"`);
  return configs[platform];
};
